import type { ReactNode, MouseEventHandler } from 'react';
import { useMediaQuery } from '@mui/material';

import { styled } from '@classify/react-core-components/styles';
import Avatar from '@classify/react-core-components/Avatar';
import Box from '@classify/react-core-components/Box';
import Hyperlink from '@classify/react-core-components/Hyperlink';
import { useTheme } from '@classify/react-core-components/styles';

import { translate } from '@openclassrooms/translations';
import { getPathName, ROUTES } from '@openclassrooms/routing';
import type { User } from '@openclassrooms/sdk-web';
import { HeaderContactButton } from '@openclassrooms/common-app/contact/components';
import { featureDecisions } from '@openclassrooms/utils/feature-flags';

import BaseHeader from '../../components/BaseHeader';
import type { BackCta } from '../../index';

interface Props {
  backCta?: BackCta;
  hasSmallLogo?: boolean;
  headline?: ReactNode;
  isBusiness?: boolean;
  isWide?: boolean;
  moreContent?: ReactNode;
  onClickAvatar?: MouseEventHandler<HTMLAnchorElement>;
  onClickLogo?: MouseEventHandler<HTMLAnchorElement>;
  shouldHideLogo?: boolean;
  user?: User | null;
}

const Headline = styled('div')(({ theme: { typography, spacing } }) => ({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  marginLeft: spacing(2),
  ...typography.subheading,
}));

function SimpleHeader({
  backCta,
  hasSmallLogo = false,
  headline,
  isBusiness = false,
  isWide = false,
  moreContent,
  onClickAvatar = () => {},
  onClickLogo = () => {},
  shouldHideLogo = false,
  user,
}: Props) {
  const theme = useTheme();
  const shouldUseSmallButton = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <BaseHeader
      shouldHideLogo={shouldHideLogo}
      hasSmallLogo={hasSmallLogo}
      isWide={isWide}
      onClickLogo={onClickLogo}
      isBusiness={isBusiness}
      backCta={backCta}
    >
      <Box sx={{ width: '100%', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <Headline>{headline}</Headline>

        {moreContent && <Box sx={{ display: 'flex', alignItems: 'center' }}>{moreContent}</Box>}

        {featureDecisions.isHeaderContactPanelEnabled() && (
          <HeaderContactButton isSmall={shouldUseSmallButton} />
        )}

        {user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ...(moreContent
                ? {
                    '&:before': {
                      content: '""',
                      height: 4,
                      width: 1,
                      backgroundColor: 'layout.line.divider',
                      mx: 2,
                    },
                  }
                : {}),
            }}
          >
            <Hyperlink
              href={getPathName(isBusiness ? ROUTES.BUSINESS.INDEX : ROUTES.DASHBOARD.INDEX)}
              title={translate('student_space.page_title')}
              onClick={onClickAvatar}
              sx={{ textDecoration: 'none' }}
            >
              <Avatar userId={user.id} userName={user.displayName} src={user.profilePicture} />
            </Hyperlink>
          </Box>
        )}
      </Box>
    </BaseHeader>
  );
}

export default SimpleHeader;
